import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Beaker,
  ShieldCheck,
  Globe2,
  FlaskConical,
  Truck,
  Microscope,
  Factory,
  Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { resolveImage } from "@/lib/api";
import { useSiteSettings, whatsappLink } from "@/lib/SiteSettingsContext";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [content, setContent] = useState(null);
  const { settings } = useSiteSettings();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data || []));
    api.get("/products", { params: { featured: true, limit: 8 } }).then(({ data }) =>
      setFeatured(data?.items || [])
    );
    api.get("/homepage-content").then(({ data }) => setContent(data));
  }, []);

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;
  }

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0A2540] text-white overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1920&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/95 to-[#0A2540]/60" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-12 py-16 sm:py-20 md:py-28 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 border border-white/20 bg-white/5 backdrop-blur-sm text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] mb-6 sm:mb-8">
                <span className="w-1.5 h-1.5 bg-[#0066FF] rounded-full animate-pulse" />
                {content.hero_badge}
              </div>
              <h1 className="heading-font text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight">
                {content.hero_title_line1}
                <br />
                {content.hero_title_line2}
                <br />
                <span className="text-[#0066FF]">{content.hero_title_highlight}</span>
                <br />
                {content.hero_title_line4}
              </h1>
              <p className="mt-6 sm:mt-8 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed whitespace-pre-line">
                {content.hero_subtitle}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/urunler"
                  className="inline-flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-white hover:text-[#0A2540] text-white px-6 sm:px-8 py-3.5 sm:py-4 font-semibold transition-colors group"
                  data-testid="hero-cta-products"
                >
                  {content.hero_cta_primary}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={whatsappLink(settings.whatsapp)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 font-semibold transition-colors"
                  data-testid="hero-cta-whatsapp"
                >
                  {content.hero_cta_secondary}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative animate-slide-in-right">
                <div className="absolute -inset-4 bg-[#0066FF]/20 blur-3xl hidden lg:block" />
                <div className="relative grid grid-cols-2 gap-2 sm:gap-3">
                  {[
                    { v: content.stat1_value, l: content.stat1_label },
                    { v: content.stat2_value, l: content.stat2_label },
                    { v: content.stat3_value, l: content.stat3_label },
                    { v: content.stat4_value, l: content.stat4_label },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 backdrop-blur-md p-4 sm:p-6">
                      <div className="text-2xl sm:text-3xl lg:text-4xl heading-font text-[#0066FF]">{s.v}</div>
                      <div className="text-[10px] sm:text-xs text-white/60 mt-1.5 sm:mt-2 uppercase tracking-wider">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Bottom border indicator strip */}
        <div className="relative grid grid-cols-3 border-t border-white/10">
          {[
            { icon: Globe2, label: "Global Tedarik Ağı" },
            { icon: FlaskConical, label: "Ar-Ge Laboratuvarı" },
            { icon: ShieldCheck, label: "ISO Sertifikalı Kalite" },
          ].map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-6 py-5 text-sm text-white/70 ${
                i < 2 ? "border-r border-white/10" : ""
              }`}
            >
              <s.icon size={18} className="text-[#0066FF]" />
              <span className="hidden sm:inline">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
                {content.categories_eyebrow}
              </div>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl text-[#0A2540] max-w-2xl">
                {content.categories_title}
              </h2>
            </div>
            <Link
              to="/urunler"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:text-[#0066FF] transition-colors"
              data-testid="categories-view-all"
            >
              Tüm Kategoriler <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/urunler?kategori=${cat.slug}`}
                className="group relative overflow-hidden border border-slate-200 hover:border-[#0066FF] bg-white transition-all"
                data-testid={`category-card-${cat.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/0 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-wider text-[#0066FF] font-semibold mb-2">
                    {cat.product_count} ürün
                  </div>
                  <h3 className="heading-font text-lg text-[#0A2540] mb-2 group-hover:text-[#0066FF] transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{cat.description}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540] group-hover:text-[#0066FF]">
                    İncele <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="py-24 bg-slate-50" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 lg:px-12">
            <div className="flex items-end justify-between mb-14">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
                  {content.featured_eyebrow}
                </div>
                <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl text-[#0A2540]">
                  {content.featured_title}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={`/urun/${p.slug}`}
                  className="group bg-white border border-slate-200 hover:border-[#0066FF] transition-colors"
                  data-testid={`featured-product-${p.slug}`}
                >
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    <img
                      src={resolveImage(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="heading-font text-sm text-[#0A2540] group-hover:text-[#0066FF] transition-colors">
                      {p.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-1">
                      {p.short_description}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="py-24 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
              {content.why_eyebrow}
            </div>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl text-[#0A2540] leading-[1.1]">
              {content.why_title}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">
              {content.why_description}
            </p>
            <div className="mt-8 space-y-3 text-sm text-[#0A2540]">
              <div className="flex items-center gap-3">
                <ShieldCheck size={16} className="text-[#0066FF]" />
                ISO 9001:2008 — Kalite Yönetim Sistemi
              </div>
              <div className="flex items-center gap-3">
                <ShieldCheck size={16} className="text-[#0066FF]" />
                ISO 14001 — Çevre Yönetim Sistemi
              </div>
              <div className="flex items-center gap-3">
                <ShieldCheck size={16} className="text-[#0066FF]" />
                OHSAS 18001:2007 — İş Sağlığı ve Güvenliği
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: Globe2,
                title: "Global Tedarik Ağı",
                desc: "Almanya, İtalya, Güney Kore, Hindistan ve Çin başta olmak üzere 15 ülkeden direkt ithalat.",
              },
              {
                icon: Microscope,
                title: "Ar-Ge Laboratuvarı",
                desc: "Müşteriye özel formülasyon geliştirme. Test edilmeden teslim etmiyoruz.",
              },
              {
                icon: Factory,
                title: "Fason Üretim",
                desc: "2000m² tesiste polimerizasyon, esterleşme ve özel reaksiyonlar.",
              },
              {
                icon: Sparkles,
                title: "29 Sektör",
                desc: "Kozmetikten dökümhaneye, tekstilden tarıma kadar geniş yelpaze.",
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 p-7 hover:border-[#0066FF] hover:bg-white transition-all"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-11 h-11 bg-[#0A2540] text-white flex items-center justify-center mb-5">
                  <f.icon size={20} />
                </div>
                <h3 className="heading-font text-lg text-[#0A2540] mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-[#0A2540] text-white relative overflow-hidden" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
              {content.process_eyebrow}
            </div>
            <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
              {content.process_title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              {
                num: "01",
                icon: Beaker,
                title: "İhtiyaç Analizi",
                desc: "Sektörünüz, beklediğiniz özellikler, kapasite ve kalite standartlarınız detaylıca analiz edilir.",
              },
              {
                num: "02",
                icon: Microscope,
                title: "Ar-Ge ve Formülasyon",
                desc: "Laboratuvarımızda ihtiyaçlarınıza özel formülasyon geliştirilir, gerekirse yeni bileşenler ithal edilir.",
              },
              {
                num: "03",
                icon: Factory,
                title: "Üretim ve Kalite Kontrolü",
                desc: "ISO sertifikalı tesisimizde seri üretim. Her aşamada kalite kontrol testleri uygulanır.",
              },
              {
                num: "04",
                icon: Truck,
                title: "Teslimat ve Teknik Destek",
                desc: "Güvenli paketleme ve hızlı lojistik ağımızla teslim. Türkiye geneli teknik destek.",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-[#0A2540] p-8 hover:bg-[#0066FF]/10 transition-colors"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="heading-font text-5xl text-[#0066FF]/40">{s.num}</span>
                  <s.icon size={26} className="text-[#0066FF]" />
                </div>
                <h3 className="heading-font text-xl mb-3">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="bottom-cta">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="border border-slate-200 p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="heading-font text-3xl md:text-4xl text-[#0A2540] leading-tight">
                {content.cta_title}
              </h2>
              <p className="mt-4 text-slate-600 whitespace-pre-line">
                {content.cta_description}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <a
                href={whatsappLink(settings.whatsapp)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ea855] text-white px-6 py-4 font-semibold transition-colors"
                data-testid="cta-whatsapp"
              >
                WhatsApp ile İletişim
              </a>
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center gap-2 border border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white px-6 py-4 font-semibold transition-colors"
                data-testid="cta-contact-page"
              >
                İletişim Sayfası
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
